import type { SelectorAccount, SelectorGroup } from "./types";

/** The state a row shows when its account cannot publish until it is
 * reconnected. Declared here, beside the search, and re-exported by
 * `menu-parts` so the row and the haystack cannot drift apart. */
export const NEEDS_RECONNECT_LABEL = "Needs reconnect";

export const UNGROUPED_SECTION_ID = "__ungrouped__";
export const DEFAULT_UNGROUPED_LABEL = "Ungrouped";

/** Below this many accounts a search box is noise, so it is hidden unless the
 * caller asks for it explicitly. */
export const SEARCH_VISIBILITY_THRESHOLD = 6;

export interface AccountMenuSection {
  id: string;
  label: string;
  isUngrouped: boolean;
  group?: SelectorGroup;
  /** Rows to render: filtered by the query, empty while collapsed. */
  accounts: SelectorAccount[];
  /** Every member matching the query, collapse ignored. Drives the header count
   * and the empty state, so a hit inside a collapsed group still counts. */
  matched: SelectorAccount[];
  matchedCount: number;
  /** Members before filtering. */
  totalCount: number;
  /** Whether the menu renders a header for this section. Collapse lives on the
   * header, so a section without one is never collapsed. */
  showHeader: boolean;
  collapsed: boolean;
}

export interface AccountMenuModel {
  sections: AccountMenuSection[];
  /** Flattened matches across every section, collapsed ones included. */
  visibleAccounts: SelectorAccount[];
  totalAccounts: number;
  showGroupHeaders: boolean;
  hasQuery: boolean;
}

export interface BuildAccountMenuModelOptions {
  accounts: SelectorAccount[];
  groups?: SelectorGroup[];
  query?: string;
  collapsedGroupIds?: ReadonlySet<string>;
  ungroupedLabel?: string;
  /** Named groups get a header even when there is nothing to separate them
   * from: a group that is itself a choice still has to be shown as one. */
  namedGroupHeaders?: boolean;
}

/**
 * Resolve the group list and the accounts that fall outside it. Groups may
 * carry their own `accounts`, or be matched against each account's `groupId` /
 * `groupLabel`; with no groups at all the labels on the accounts imply them.
 */
export function normalizeAccountGroups(
  accounts: SelectorAccount[],
  groups: SelectorGroup[]
): { groups: SelectorGroup[]; ungrouped: SelectorAccount[] } {
  const normalized =
    groups.length > 0
      ? groups.map((group) => ({
          ...group,
          accounts:
            group.accounts ||
            accounts.filter(
              (account) =>
                account.groupId === group.id ||
                account.groupLabel === group.label
            ),
        }))
      : Array.from(
          new Set(accounts.map((account) => account.groupLabel).filter(Boolean))
        ).map((label) => ({
          id: label as string,
          label: label as string,
          accounts: accounts.filter((account) => account.groupLabel === label),
        }));

  const groupedIds = new Set(
    normalized.flatMap((group) =>
      (group.accounts || []).map((account) => account.id)
    )
  );

  return {
    groups: normalized,
    ungrouped: accounts.filter((account) => !groupedIds.has(account.id)),
  };
}

/** The single normalisation for every query comparison in this family:
 * trimmed, whitespace-collapsed, lowercased. Anything that filters on the
 * query must use it, or two rows answer the same typing differently. */
export function normalizeQuery(query: string | undefined): string {
  return (query || "").trim().replace(/\s+/g, " ").toLowerCase();
}

function accountMatches(
  account: SelectorAccount,
  needle: string,
  sectionLabel: string
): boolean {
  if (!needle) return true;
  const haystacks = [
    account.label,
    // Both, not one instead of the other. The handle is back on the visible
    // second line of a disconnected row, so it stays searchable, and the state
    // is on the row too, so typing "reconnect" has to find it.
    account.secondaryText,
    account.disconnected ? NEEDS_RECONNECT_LABEL : undefined,
    account.searchText,
    account.groupLabel,
    sectionLabel,
  ];
  return haystacks.some((value) =>
    (value || "").toLowerCase().includes(needle)
  );
}

/**
 * The single source of truth for what an account menu renders: which sections
 * exist, which rows survive the query, which headers are worth showing and what
 * each header counts. Both the single- and multi-select menus consume it, so
 * their grouping and filtering cannot drift apart.
 */
export function buildAccountMenuModel({
  accounts,
  groups = [],
  query,
  collapsedGroupIds,
  ungroupedLabel = DEFAULT_UNGROUPED_LABEL,
  namedGroupHeaders = false,
}: BuildAccountMenuModelOptions): AccountMenuModel {
  const needle = normalizeQuery(query);
  const hasQuery = needle.length > 0;
  const { groups: namedGroups, ungrouped } = normalizeAccountGroups(
    accounts,
    groups
  );

  const collapsedIds = collapsedGroupIds ?? new Set<string>();

  const buildSection = (
    id: string,
    label: string,
    members: SelectorAccount[],
    isUngrouped: boolean,
    group?: SelectorGroup
  ): AccountMenuSection => {
    const matched = members.filter((account) =>
      accountMatches(account, needle, label)
    );
    return {
      id,
      label,
      isUngrouped,
      group,
      accounts: matched,
      matched,
      matchedCount: matched.length,
      totalCount: members.length,
      showHeader: false,
      collapsed: false,
    };
  };

  const sections: AccountMenuSection[] = namedGroups.map((group) =>
    buildSection(group.id, group.label, group.accounts || [], false, group)
  );

  if (ungrouped.length > 0) {
    // Ungrouped is always last: it is the remainder, not a peer of the named
    // groups.
    sections.push(
      buildSection(UNGROUPED_SECTION_ID, ungroupedLabel, ungrouped, true)
    );
  }

  // A group with no members at all is not a section, it is a stale record; a
  // group with no matches disappears for the duration of the query.
  const rendered = sections.filter((section) =>
    hasQuery ? section.matchedCount > 0 : section.totalCount > 0
  );

  // A header earns its place only when it separates something, and that has to
  // be judged on the sections that actually RENDER. Counting dropped ones put a
  // lone header over a plain list whenever a caller passed a group record whose
  // members had all gone.
  const renderedNamed = rendered.filter((section) => !section.isUngrouped);
  const hasRenderedUngrouped = rendered.some((section) => section.isUngrouped);
  const showGroupHeaders =
    renderedNamed.length > 1 ||
    (renderedNamed.length === 1 && hasRenderedUngrouped);

  // The collapse store is shared by every selector, so only a section with a
  // header to reopen it may read it. A query always expands.
  const sectionsWithHeaders = rendered.map((section) => {
    const showHeader =
      showGroupHeaders || (namedGroupHeaders && !section.isUngrouped);
    const collapsed = showHeader && !hasQuery && collapsedIds.has(section.id);
    return {
      ...section,
      showHeader,
      collapsed,
      accounts: collapsed ? [] : section.accounts,
    };
  });

  return {
    sections: sectionsWithHeaders,
    visibleAccounts: sections.flatMap((section) => section.matched),
    totalAccounts: accounts.length,
    showGroupHeaders,
    hasQuery,
  };
}

/** Whether the search input should render, given the caller's explicit wish. */
export function shouldShowSearch(
  searchable: boolean | undefined,
  totalAccounts: number
): boolean {
  if (searchable !== undefined) return searchable;
  return totalAccounts >= SEARCH_VISIBILITY_THRESHOLD;
}

/**
 * First case-insensitive hit of `query` in `text`, as `[start, end)`.
 * Deliberately not a regex: the app's existing highlight helper uses a `/g`
 * regex with `.test()`, whose `lastIndex` carry-over makes alternate calls miss.
 */
export function matchRanges(
  text: string | undefined,
  query: string | undefined
): [number, number] | null {
  const needle = normalizeQuery(query);
  if (!needle || !text) return null;
  const index = text.toLowerCase().indexOf(needle);
  if (index < 0) return null;
  return [index, index + needle.length];
}
